import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { PageState } from '../app/PublicLayout.jsx'
import { thumbUrl, useCollection } from '../app/useSiteData.js'
import { formatDate } from '../lib/format.js'
import { useI18n } from '../lib/i18n.jsx'
import { Chip, Container, EmptyState, Media, PageHead, Row, RowItem, RowList, Section } from '../ui/index.js'

/**
 * News index.
 *
 * A flat hairline-separated list, not a grid of cards. Cards give every item
 * the same weight and cost roughly three times the vertical space per entry;
 * this list keeps twenty dated rows scannable on one screen, which is what a
 * visitor actually does with a news page — scan for a date and a headline.
 *
 * The left column is the ISO date in tabular figures rather than a localised
 * string: `2026-08-12` is the same width on every row, so the headlines form a
 * clean edge. The spelled-out date is still available, as the `title` and to a
 * screen reader through `<time datetime>`.
 *
 * 新闻列表页。
 *
 * 用发丝线分隔的扁平列表，而不是卡片栅格。卡片让所有条目等权，且每条大约多占三倍
 * 纵向空间；这个列表能让二十条带日期的行在一屏内可扫读——这正是访客对新闻页真正做的
 * 事：扫日期和标题。
 *
 * 左列是等宽数字的 ISO 日期而非本地化字符串：2026-08-12 在每一行宽度相同，标题因此
 * 对齐成一条干净的边。完整写法的日期仍然在 title 属性里，也通过 <time datetime>
 * 提供给读屏软件。
 */

/**
 * Sort newest-first with pinned items lifted to the top.
 *
 * The server already orders by date; pinning is a display decision, so it is
 * applied here rather than asked of the API.
 *
 * 按时间倒序排列，置顶项提到最前。服务端已按日期排好；置顶是展示层的决定，
 * 因此在这里处理，而不是去要求接口。
 *
 * @param {object[]} records News records / 新闻记录。
 * @returns {object[]} Ordered copy / 排序后的副本。
 */
function orderNews(records) {
  return [...records].sort((a, b) => {
    if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1
    return String(b.date || '').localeCompare(String(a.date || ''))
  })
}

/**
 * One row of the news list.
 *
 * Exported because the home page shows the three newest items in exactly this
 * format. Two renderings of the same record that drift apart is how a site
 * starts to look assembled rather than designed.
 *
 * 新闻列表中的一行。导出是因为首页要以完全相同的格式展示最新三条。同一条记录的
 * 两种渲染一旦走样，站点就会从「设计过」滑向「拼出来」。
 *
 * @param {object} props Props / 属性。
 * @param {object} props.record News record / 新闻记录。
 * @returns {JSX.Element} Row / 列表行。
 */
export function NewsRow({ record }) {
  const { t, pick, lang } = useI18n()
  const iso = String(record.date || '').slice(0, 10)
  const summary = pick(record.summary)
  const cover = thumbUrl(record.cover)
  const tags = Array.isArray(record.tags) ? record.tags : []

  return (
    <RowItem
      className="news-row"
      meta={
        <>
          <time className="news-row__date" dateTime={iso} title={formatDate(record.date, lang)}>
            {iso}
          </time>
          {record.pinned && <span className="news-row__pin">{t('status.pinned')}</span>}
        </>
      }
    >
      <h3 className="news-row__title">
        <Link to={`/news/${record.id}`}>{pick(record.title)}</Link>
      </h3>
      {summary && <p className="news-row__summary">{summary}</p>}
      {tags.length > 0 && (
        <Row className="news-row__tags" gap="tight">
          {tags.map((tag) => (
            <Chip key={tag} className="news-row__tag">
              {tag}
            </Chip>
          ))}
        </Row>
      )}
      {/* The cover is decorative here — the headline beside it already names the
          story, so an alt text would only repeat it to a screen reader.
          这里的封面是装饰性的——旁边的标题已经说明了内容，alt 文本只会对读屏软件
          重复一遍。 */}
      {cover && (
        <Media className="news-row__thumb">
          <img src={cover} alt="" loading="lazy" decoding="async" />
        </Media>
      )}
    </RowItem>
  )
}

/**
 * News index page.
 *
 * 新闻列表页。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function NewsList() {
  const { t } = useI18n()
  const { data: records, loading, error, reload } = useCollection('news')
  const [year, setYear] = useState('')

  const ordered = useMemo(() => orderNews(records || []), [records])

  // Years come from the data, so a chip can never filter the list to nothing.
  // 年份取自数据，因此任何一个筛选片都不可能把列表筛空。
  const years = useMemo(() => {
    const seen = new Set(ordered.map((record) => String(record.date || '').slice(0, 4)).filter(Boolean))
    return [...seen].sort((a, b) => b.localeCompare(a))
  }, [ordered])

  const visible = year ? ordered.filter((record) => String(record.date || '').startsWith(year)) : ordered

  return (
    <Section className="news-page">
      <Container width="narrow">
        <PageHead
          title={t('news.title')}
          lede={t('news.lede')}
          actions={
            years.length > 1 && (
              <Row className="news-years" gap="tight">
                <Chip className="news-years__chip" active={year === ''} onClick={() => setYear('')}>
                  {t('common.all')}
                </Chip>
                {years.map((value) => (
                  <Chip
                    key={value}
                    className="news-years__chip"
                    active={year === value}
                    onClick={() => setYear(value)}
                  >
                    {value}
                  </Chip>
                ))}
              </Row>
            )
          }
        />

        <PageState
          loading={loading}
          error={error}
          onRetry={reload}
          isEmpty={ordered.length === 0}
          empty={<EmptyState title={t('news.empty')} body={t('news.emptyBody')} />}
        >
          <RowList className="news-list">
            {visible.map((record) => (
              <NewsRow key={record.id} record={record} />
            ))}
          </RowList>
        </PageState>
      </Container>
    </Section>
  )
}
